import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Voicemail, Search, Filter, CalendarIcon, Play, User, Building, MapPin, Tag, Clock, RefreshCw, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  completed: { label: 'Answered', color: 'bg-green-100 text-green-800', icon: PhoneIncoming },
  missed: { label: 'Missed', color: 'bg-red-100 text-red-800', icon: PhoneMissed },
  voicemail: { label: 'Voicemail', color: 'bg-purple-100 text-purple-800', icon: Voicemail },
  abandoned: { label: 'Abandoned', color: 'bg-orange-100 text-orange-800', icon: PhoneMissed },
  busy: { label: 'Busy', color: 'bg-yellow-100 text-yellow-800', icon: Phone }
};

const regionConfig = {
  JAX: { name: 'Jacksonville', color: 'bg-blue-100 text-blue-800' },
  ORL: { name: 'Orlando', color: 'bg-purple-100 text-purple-800' },
  TAM: { name: 'Tampa', color: 'bg-green-100 text-green-800' },
  WPB: { name: 'West Palm Beach', color: 'bg-orange-100 text-orange-800' },
  NAP: { name: 'Naples', color: 'bg-cyan-100 text-cyan-800' },
  MIA: { name: 'Miami', color: 'bg-pink-100 text-pink-800' }
};

export default function CallCenterCallLog() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedCall, setSelectedCall] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCalls, setTotalCalls] = useState(0);
  const [sources, setSources] = useState([]);
  const [regions, setRegions] = useState([]);

  const fetchCalls = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', 50);
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (regionFilter !== 'all') params.append('region', regionFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (directionFilter !== 'all') params.append('direction', directionFilter);
      if (dateRange.from) params.append('start_date', format(dateRange.from, 'yyyy-MM-dd'));
      if (dateRange.to) params.append('end_date', format(dateRange.to, 'yyyy-MM-dd'));
      
      const response = await axios.get(`${API}/call-center/interactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCalls(response.data.interactions || []);
      setTotalCalls(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch calls:', err);
      toast.error('Failed to load call log');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, regionFilter, sourceFilter, directionFilter, dateRange]);

  const fetchFilters = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [sourcesRes, regionsRes] = await Promise.all([
        axios.get(`${API}/settings/call-center/sources`, { headers }),
        axios.get(`${API}/settings/call-center/regions`, { headers })
      ]);
      
      setSources(sourcesRes.data.sources || []);
      setRegions(regionsRes.data.regions || []);
    } catch (err) {
      console.error('Failed to fetch filters:', err);
    }
  }, []);

  useEffect(() => {
    fetchCalls();
    fetchFilters();
  }, [fetchCalls, fetchFilters]);

  const filteredCalls = calls.filter(call => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      call.caller_number?.includes(search) ||
      call.caller_name?.toLowerCase().includes(search) ||
      call.contact_name?.toLowerCase().includes(search) ||
      call.tracking_number_name?.toLowerCase().includes(search)
    );
  });

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString();
  };

  const getIcon = (call) => {
    if (call.status === 'missed') return PhoneMissed;
    if (call.status === 'voicemail') return Voicemail;
    if (call.direction === 'outbound') return PhoneOutgoing;
    return PhoneIncoming;
  };

  return (
    <Layout>
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Call Log</h1>
          <p className="text-muted-foreground">
            View all calls from CallRail • {totalCalls} total calls
            {calls.some(c => c.is_backfill) && <Badge variant="outline" className="ml-2">Includes imported data</Badge>}
          </p>
        </div>
        <Button variant="outline" onClick={fetchCalls}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, number, or source..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Answered</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
                <SelectItem value="voicemail">Voicemail</SelectItem>
                <SelectItem value="abandoned">Abandoned</SelectItem>
              </SelectContent>
            </Select>

            <Select value={directionFilter} onValueChange={setDirectionFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>

            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {Object.entries(regionConfig).map(([code, config]) => (
                  <SelectItem key={code} value={code}>{config.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {sources.map(s => (
                  <SelectItem key={s.source} value={s.source}>{s.source} ({s.total_calls})</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[200px] justify-start">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d')}`
                    ) : format(dateRange.from, 'MMM d, yyyy')
                  ) : 'Select dates'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Call List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Recent Calls</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : filteredCalls.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Phone className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No calls found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCalls.map((call) => {
                const Icon = getIcon(call);
                const status = statusConfig[call.status] || statusConfig.completed;
                const region = regionConfig[call.region];
                
                return (
                  <div
                    key={call.id}
                    onClick={() => setSelectedCall(call)}
                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                  >
                    <div className={`p-2 rounded-full ${call.status === 'missed' ? 'bg-red-100' : call.direction === 'inbound' ? 'bg-green-100' : 'bg-blue-100'}`}>
                      <Icon className={`h-5 w-5 ${call.status === 'missed' ? 'text-red-600' : call.direction === 'inbound' ? 'text-green-600' : 'text-blue-600'}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">
                          {call.caller_name || call.contact_name || call.caller_number || 'Unknown'}
                        </span>
                        {call.is_backfill && (
                          <Badge variant="outline" className="text-xs">Imported</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                        <span>{call.caller_number}</span>
                        {call.tracking_number_name && (
                          <>
                            <span>•</span>
                            <span className="truncate max-w-[200px]">{call.tracking_number_name}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {region && (
                        <Badge variant="outline" className={region.color}>
                          <MapPin className="h-3 w-3 mr-1" />
                          {call.region}
                        </Badge>
                      )}
                      {call.source && (
                        <Badge variant="secondary">
                          <Tag className="h-3 w-3 mr-1" />
                          {call.source}
                        </Badge>
                      )}
                    </div>

                    <div className="text-right">
                      <Badge className={status.color}>
                        {status.label}
                      </Badge>
                      <div className="text-xs text-muted-foreground mt-1">
                        {formatTime(call.started_at)}
                      </div>
                    </div>

                    <div className="text-right text-sm w-16">
                      <div className="font-medium">{formatDuration(call.duration_seconds)}</div>
                      {call.recording_url && (
                        <Play className="h-4 w-4 text-muted-foreground mx-auto mt-1" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalCalls > 50 && (
            <div className="flex justify-center gap-2 mt-4 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <span className="py-2 px-4 text-sm">
                Page {page} of {Math.ceil(totalCalls / 50)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= Math.ceil(totalCalls / 50)}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Call Detail Modal */}
      <Dialog open={!!selectedCall} onOpenChange={() => setSelectedCall(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Call Details</DialogTitle>
          </DialogHeader>
          {selectedCall && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="p-3 rounded-full bg-primary/10">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">
                    {selectedCall.caller_name || selectedCall.contact_name || 'Unknown Caller'}
                  </h3>
                  <p className="text-muted-foreground">{selectedCall.caller_number}</p>
                </div>
                <Badge className={statusConfig[selectedCall.status]?.color}>
                  {statusConfig[selectedCall.status]?.label || selectedCall.status}
                </Badge>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Direction</label>
                  <p className="font-medium capitalize">{selectedCall.direction}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Duration</label>
                  <p className="font-medium">{formatDuration(selectedCall.duration_seconds)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Date & Time</label>
                  <p className="font-medium">{formatTime(selectedCall.started_at)}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Tracking Number</label>
                  <p className="font-medium">{selectedCall.tracking_number || '-'}</p>
                </div>
                {selectedCall.tracking_number_name && (
                  <div className="col-span-2">
                    <label className="text-sm text-muted-foreground">Number Name</label>
                    <p className="font-medium">{selectedCall.tracking_number_name}</p>
                  </div>
                )}
                {selectedCall.source && (
                  <div>
                    <label className="text-sm text-muted-foreground">Source</label>
                    <Badge variant="secondary">{selectedCall.source}</Badge>
                  </div>
                )}
                {selectedCall.region && (
                  <div>
                    <label className="text-sm text-muted-foreground">Region</label>
                    <Badge variant="outline" className={regionConfig[selectedCall.region]?.color}>
                      {regionConfig[selectedCall.region]?.name || selectedCall.region}
                    </Badge>
                  </div>
                )}
                {selectedCall.campaign && (
                  <div>
                    <label className="text-sm text-muted-foreground">Campaign</label>
                    <p className="font-medium">{selectedCall.campaign}</p>
                  </div>
                )}
                {selectedCall.medium && (
                  <div>
                    <label className="text-sm text-muted-foreground">Medium</label>
                    <p className="font-medium">{selectedCall.medium}</p>
                  </div>
                )}
              </div>

              {/* Recording */}
              {selectedCall.recording_url && (
                <div className="pt-4 border-t">
                  <label className="text-sm text-muted-foreground">Recording</label>
                  <audio controls className="w-full mt-2">
                    <source src={selectedCall.recording_url} />
                  </audio>
                </div>
              )}

              {/* Transcript */}
              {selectedCall.transcript && (
                <div className="pt-4 border-t">
                  <label className="text-sm text-muted-foreground">Transcript</label>
                  <p className="text-sm bg-muted p-3 rounded mt-2 max-h-40 overflow-y-auto">
                    {selectedCall.transcript}
                  </p>
                </div>
              )}

              {/* Linked Records */}
              <div className="pt-4 border-t space-y-2">
                {selectedCall.contact_id && (
                  <Button variant="outline" className="w-full justify-start">
                    <User className="h-4 w-4 mr-2" />
                    View Contact: {selectedCall.contact_name}
                    <ExternalLink className="h-4 w-4 ml-auto" />
                  </Button>
                )}
                {selectedCall.job_id && (
                  <Button variant="outline" className="w-full justify-start">
                    <Building className="h-4 w-4 mr-2" />
                    View Job: {selectedCall.job_title || selectedCall.job_id}
                    <ExternalLink className="h-4 w-4 ml-auto" />
                  </Button>
                )}
              </div>

              {selectedCall.is_backfill && (
                <div className="text-xs text-muted-foreground text-center">
                  This call was imported from historical CallRail data
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedCall(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
